import type { SVGAttributes } from 'react';
import { cn } from './cn';

const SIZES = { sm: 14, md: 16, lg: 24 } as const;

export interface SpinnerProps extends Omit<
  SVGAttributes<SVGSVGElement>,
  'children'
> {
  size?: keyof typeof SIZES;
  /**
   * Announced to assistive technology. Omit when the spinner is decorative
   * (for example inside a button that already sets `aria-busy`).
   */
  label?: string;
}

/** Indeterminate activity indicator. Inherits the current text color. */
export const Spinner = ({
  size = 'md',
  label,
  className,
  ...props
}: SpinnerProps) => {
  const px = SIZES[size];
  const svg = (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={cn('shrink-0 animate-spin', className)}
      {...props}
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="3"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
  if (!label) return svg;
  return (
    <span role="status" className="inline-flex items-center">
      {svg}
      <span className="sr-only">{label}</span>
    </span>
  );
};
